import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { IChatConversationUser, IConversationMessageReadBy } from '@fi-shared/fi-chat/fi-chat';
import { FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-conversation-read-by-badge',
    templateUrl: './conversation-read-by-badge.component.html',
    styleUrls: ['./conversation-read-by-badge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-conversation-read-by-badge' },
    standalone: false,
})
export class FiConversationReadByBadgeComponent {
    @Input({ required: true }) conversationMessageReadBy: IConversationMessageReadBy[];
    @Input({ required: true }) conversationUsers: IChatConversationUser[];
    protected readonly maxReadByProfilesShown = 4;

    protected isAdditionalReadersPopupShown: boolean = false;
    protected readonly FiProfileSizes = FiProfileSizes;
    private currentReadStatusPopupUserId: number;

    constructor(
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    protected isReadStatusPopupShown(userId: number): boolean {
        return this.currentReadStatusPopupUserId === userId;
    }

    protected toggleInfoShownStatus(userId: number, status?: boolean): void {
        if (isDefined(status)) {
            this.currentReadStatusPopupUserId = status ? userId : undefined;
        } else {
            this.currentReadStatusPopupUserId = this.currentReadStatusPopupUserId === userId ? undefined : userId;
        }
    }

    protected toggleAdditionalReadersPopupShown(status?: boolean): void {
        this.currentReadStatusPopupUserId = undefined;
        this.isAdditionalReadersPopupShown = isDefined(status) ? status : !this.isAdditionalReadersPopupShown;
    }

    protected isAdditionalReadersShown(): boolean {
        return this.conversationMessageReadBy.length > this.maxReadByProfilesShown;
    }

    protected getAdditionalReadersCount(): string {
        return `+${this.conversationMessageReadBy.length - this.maxReadByProfilesShown}`;
    }

    protected getPersonalChatInfoPopupText(readByUserData: IConversationMessageReadBy): string {
        const readByProfileData = this.getProfileData(readByUserData);
        if (!readByProfileData) return '';
        return (
            this.appTextService.get('chat.conversation_message_read_by', {
                '{0}': readByProfileData.FullName,
            }) +
            ' ' +
            this.fiFormatDateService.formatDate(readByUserData.ReadOn, FiDateTimeFormatOptions)
        );
    }

    protected getProfileData(readByUserData: IConversationMessageReadBy): IChatConversationUser {
        return this.conversationUsers.find((conversationUser) => conversationUser.Id === readByUserData.ReadByUserId);
    }

    protected getAdditionalReadersUserData(): IChatConversationUser[] {
        return this.conversationMessageReadBy
            .map((readByUserData, index) => {
                if (index >= this.maxReadByProfilesShown) return this.getProfileData(readByUserData);
            })
            .filter((readByUserData) => readByUserData);
    }
}
